import { useState } from 'react';
import api from '../../api';
import { Building2, User } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { FormLabel } from '../../components/ui/FormLabel';

export const getClientDisplayName = (client) => {
  if (!client) return '';
  if (client.client_type === 'company') {
    return client.company_name || 'Unnamed Company';
  }
  return `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Unnamed Individual';
};

/** Default on when key absent (aligned with API + schedulers). */
export const isClientInvoiceReminderEmailsEnabled = (client) =>
  client?.preferences?.invoiceReminderEmailsEnabled !== false;

export const ContactForm = ({ clientId, contact, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: contact?.first_name || '',
    last_name: contact?.last_name || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    mobile: contact?.mobile || '',
    position: contact?.position || '',
    notes: contact?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (contact) {
        await api.put(`/clients/${clientId}/contacts/${contact.id}`, formData);
      } else {
        await api.post(`/clients/${clientId}/contacts`, formData);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-lg)] max-w-md w-full p-6 shadow-[var(--ds-shadow-card)]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-h2 font-semibold text-[var(--ds-text-primary)]">
            {contact ? 'Edit Contact' : 'Add Contact'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] text-2xl transition-colors"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)] text-xs text-[var(--ds-status-danger)]">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <FormLabel required>First Name</FormLabel>
              <Input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
                className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <FormLabel>Last Name</FormLabel>
              <Input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>

          <div>
            <FormLabel>Email</FormLabel>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <FormLabel>Phone</FormLabel>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <FormLabel>Mobile</FormLabel>
              <Input
                type="tel"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>

          <div>
            <FormLabel>Position/Role</FormLabel>
            <Input
              type="text"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              placeholder="e.g., Accounts Payable, Project Manager"
              className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <FormLabel>Notes</FormLabel>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="text-xs border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={loading}>
              {loading ? 'Saving...' : contact ? 'Update' : 'Add'} Contact
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const ClientForm = ({ client, onClose, onSuccess }) => {
  const isEdit = !!client;

  const [formData, setFormData] = useState({
    client_type: client?.client_type || 'individual',
    company_name: client?.company_name || '',
    first_name: client?.first_name || '',
    last_name: client?.last_name || '',
    email: client?.email || '',
    phone: client?.phone || '',
    mobile: client?.mobile || '',
    address_line1: client?.address_line1 || '',
    address_line2: client?.address_line2 || '',
    city: client?.city || '',
    county: client?.county || '',
    postcode: client?.postcode || '',
    country: client?.country || 'GB',
    credit_limit: client?.credit_limit || '',
    current_balance: client?.current_balance || '',
    payment_terms_days:
      client?.payment_terms_days != null && client.payment_terms_days !== ''
        ? String(client.payment_terms_days)
        : '7',
    notes: client?.notes || '',
    is_active: client?.is_active !== undefined ? client.is_active : true,
    invoiceReminderEmailsEnabled: isClientInvoiceReminderEmailsEnabled(client),
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const parsedTerms = parseInt(formData.payment_terms_days, 10);
      const payment_terms_days = Number.isFinite(parsedTerms) ? parsedTerms : 7;
      const { invoiceReminderEmailsEnabled, ...restForm } = formData;
      const payload = { ...restForm, payment_terms_days };
      const preferences = { invoiceReminderEmailsEnabled };

      if (isEdit) {
        await api.put(`/clients/${client.id}`, { ...payload, preferences });
      } else {
        await api.post('/clients', { ...payload, preferences });
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to save client');
      console.error('Form error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const inputClass =
    'h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)]">
          {error}
        </div>
      )}

      <div>
        <FormLabel className="mb-2" required>
          Client Type
        </FormLabel>
        <div className="flex space-x-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="client_type"
              value="individual"
              checked={formData.client_type === 'individual'}
              onChange={handleChange}
              className="mr-2 h-4 w-4 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-focus-ring)] border-[var(--ds-border-subtle)]"
            />
            <span className="flex items-center gap-1 text-xs text-[var(--ds-text-primary)]">
              <User className="h-4 w-4" /> Individual
            </span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="client_type"
              value="company"
              checked={formData.client_type === 'company'}
              onChange={handleChange}
              className="mr-2 h-4 w-4 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-focus-ring)] border-[var(--ds-border-subtle)]"
            />
            <span className="flex items-center gap-1 text-xs text-[var(--ds-text-primary)]">
              <Building2 className="h-4 w-4" /> Company
            </span>
          </label>
        </div>
      </div>

      {formData.client_type === 'company' && (
        <div>
          <FormLabel required>Company Name</FormLabel>
          <Input
            type="text"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            required
            placeholder="Smith Electrical Ltd"
            className={inputClass}
          />
        </div>
      )}

      {formData.client_type === 'individual' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel required>First Name</FormLabel>
            <Input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
              placeholder="John"
              className={inputClass}
            />
          </div>
          <div>
            <FormLabel required>Last Name</FormLabel>
            <Input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
              placeholder="Smith"
              className={inputClass}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
            className={inputClass}
          />
        </div>
        <div>
          <FormLabel>Phone</FormLabel>
          <Input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="01234 567890"
            className={inputClass}
          />
        </div>
        <div>
          <FormLabel>Mobile</FormLabel>
          <Input
            type="tel"
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
            placeholder="07700 900123"
            className={inputClass}
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-h2 font-medium text-[var(--ds-text-primary)]">Address</h3>
        <div>
          <FormLabel>Address Line 1</FormLabel>
          <Input
            type="text"
            name="address_line1"
            value={formData.address_line1}
            onChange={handleChange}
            placeholder="123 Main Street"
            className={inputClass}
          />
        </div>
        <div>
          <FormLabel>Address Line 2</FormLabel>
          <Input
            type="text"
            name="address_line2"
            value={formData.address_line2}
            onChange={handleChange}
            placeholder="Apartment, suite, etc. (optional)"
            className={inputClass}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel>City</FormLabel>
            <Input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="London"
              className={inputClass}
            />
          </div>
          <div>
            <FormLabel>County</FormLabel>
            <Input
              type="text"
              name="county"
              value={formData.county}
              onChange={handleChange}
              placeholder="Greater London"
              className={inputClass}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <FormLabel>Postcode</FormLabel>
            <Input
              type="text"
              name="postcode"
              value={formData.postcode}
              onChange={handleChange}
              placeholder="SW1A 1AA"
              className={inputClass}
            />
          </div>
          <div>
            <FormLabel>Country</FormLabel>
            <Input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              maxLength={2}
              placeholder="GB"
              className={inputClass}
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {isEdit && formData.current_balance !== undefined && formData.current_balance !== '' && (
          <div className="bg-[var(--ds-control-field-bg)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] p-4">
            <label className="block text-micro font-medium text-[var(--ds-text-muted)] uppercase tracking-wide mb-1">
              Current Balance
            </label>
            <p
              className={`text-h1 font-semibold ${
                parseFloat(formData.current_balance || 0) >= 0
                  ? 'text-[var(--ds-text-primary)]'
                  : 'text-[var(--ds-status-danger)]'
              }`}
            >
              £{parseFloat(formData.current_balance || 0).toFixed(2)}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <FormLabel>Credit Limit (£)</FormLabel>
            <Input
              type="number"
              name="credit_limit"
              value={formData.credit_limit}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder="0.00"
              className={inputClass}
            />
          </div>
          <div>
            <FormLabel>Current Balance (£)</FormLabel>
            <Input
              type="number"
              name="current_balance"
              value={formData.current_balance}
              onChange={handleChange}
              step="0.01"
              placeholder="0.00"
              className={inputClass}
            />
          </div>
          <div className="md:col-span-2">
            <FormLabel>Payment terms (days)</FormLabel>
            <Input
              type="number"
              name="payment_terms_days"
              value={formData.payment_terms_days}
              onChange={handleChange}
              min="0"
              max="365"
              placeholder="7"
              className={inputClass}
            />
            <p className="mt-1 text-micro text-[var(--ds-text-muted)]">
              Days from invoice date until payment is due (default 7).
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-control-field-bg)]/50 p-4 space-y-2">
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="client-invoice-reminder-emails"
            name="invoiceReminderEmailsEnabled"
            checked={formData.invoiceReminderEmailsEnabled}
            onChange={handleChange}
            className="mt-0.5 h-4 w-4 shrink-0 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-focus-ring)] border-gray-300 dark:border-gray-600 rounded cursor-pointer"
          />
          <div className="min-w-0">
            <label
              htmlFor="client-invoice-reminder-emails"
              className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
            >
              Automated invoice reminders
            </label>
            <p className="text-micro text-[var(--ds-text-muted)] mt-1">
              When enabled, this client can receive automated reminder emails for sent or unpaid invoices,
              according to your tenant notification settings. Turn off to opt out for this client only.
            </p>
          </div>
        </div>
      </div>

      <div>
        <FormLabel>Notes</FormLabel>
        <Textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          placeholder="Additional notes about this client..."
          className={`text-xs ${inputClass}`}
        />
      </div>

      {isEdit && (
        <div className="flex items-center">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            className="h-4 w-4 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-focus-ring)] border-[var(--ds-border-subtle)] rounded cursor-pointer"
          />
          <FormLabel className="ml-2 mb-0" spacing={false}>
            Client is active
          </FormLabel>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t border-[var(--ds-border-divider)]">
        <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" size="sm" disabled={loading}>
          {loading ? 'Saving...' : isEdit ? 'Update Client' : 'Create Client'}
        </Button>
      </div>
    </form>
  );
};
